import React, { useState, useEffect } from 'react';
import {
  FiBookOpen, FiUpload, FiDownload, FiSearch, FiShield, FiLock, FiUsers,
  FiChevronRight, FiArrowLeft, FiRefreshCw, FiLayers, FiCode, FiTrash2, FiPlus
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import docsApi from '../../api/docsApi';
import ApiUploadModal from './ApiUploadModal';
import ApiShareModal from './ApiShareModal';
import ApiEndpointCard from './ApiEndpointCard';
import SwaggerUiViewer from './SwaggerUiViewer';

export function DocumentationPortal() {
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [activeViewMode, setActiveViewMode] = useState('catalog'); // 'catalog' | 'detail' | 'global_endpoints'
  const [docDisplayMode, setDocDisplayMode] = useState('explorer'); // 'explorer' | 'swagger'

  // Catalog State
  const [documents, setDocuments] = useState([]);
  const [isLoadingDocs, setIsLoadingDocs] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedModule, setSelectedModule] = useState('all');
  const [selectedVisibility, setSelectedVisibility] = useState('all');
  const [availableModules, setAvailableModules] = useState([]);

  // Detail State
  const [currentDocDetail, setCurrentDocDetail] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState('');
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [endpointSearch, setEndpointSearch] = useState('');
  const [endpointMethodFilter, setEndpointMethodFilter] = useState('ALL');
  const [endpointTagFilter, setEndpointTagFilter] = useState('ALL');

  // Global Endpoints State
  const [globalEndpoints, setGlobalEndpoints] = useState([]);
  const [isLoadingGlobal, setIsLoadingGlobal] = useState(false);
  const [globalQuery, setGlobalQuery] = useState('');
  const [globalMethod, setGlobalMethod] = useState('ALL');

  // Modals
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [newVersionTargetDoc, setNewVersionTargetDoc] = useState(null);
  const [shareTargetDoc, setShareTargetDoc] = useState(null);

  useEffect(() => {
    loadCatalog();
    loadModules();
  }, [selectedModule, selectedVisibility]);

  useEffect(() => {
    if (selectedDocId) {
      loadDocumentDetail(selectedDocId, selectedVersion);
    }
  }, [selectedDocId, selectedVersion]);

  const loadCatalog = async () => {
    try {
      setIsLoadingDocs(true);
      const res = await docsApi.listCatalog({
        search: searchQuery.trim() || undefined,
        module: selectedModule !== 'all' ? selectedModule : undefined,
        visibility: selectedVisibility !== 'all' ? selectedVisibility : undefined,
      });
      setDocuments(res?.content || []);
    } catch (err) {
      toast.error(err.message || 'Failed to load API catalog');
    } finally {
      setIsLoadingDocs(false);
    }
  };

  const loadModules = async () => {
    try {
      const mods = await docsApi.getModules();
      setAvailableModules(mods || []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadDocumentDetail = async (id, version) => {
    try {
      setIsLoadingDetail(true);
      const detail = await docsApi.getDocumentDetail(id, version);
      setCurrentDocDetail(detail);
      if (!selectedVersion && detail.selectedVersionNumber) {
        setSelectedVersion(detail.selectedVersionNumber);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load documentation');
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const handleOpenDoc = (docId) => {
    setSelectedDocId(docId);
    setSelectedVersion('');
    setActiveViewMode('detail');
  };

  const handleBackToCatalog = () => {
    setSelectedDocId(null);
    setCurrentDocDetail(null);
    setSelectedVersion('');
    setActiveViewMode('catalog');
    loadCatalog();
  };

  const handleDownloadFull = async () => {
    if (!currentDocDetail) return;
    try {
      await docsApi.downloadFullDocument(
        currentDocDetail.id,
        selectedVersion || currentDocDetail.selectedVersionNumber
      );
      toast.success('Downloaded full OpenAPI specification');
    } catch (err) {
      toast.error(err.message || 'Failed to download documentation');
    }
  };

  const handleDownloadEndpoint = async (endpointId) => {
    if (!currentDocDetail) return;
    try {
      await docsApi.downloadEndpoint(currentDocDetail.id, endpointId);
      toast.success('Downloaded endpoint specification slice');
    } catch (err) {
      toast.error(err.message || 'Failed to download endpoint');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this API documentation?')) return;
    try {
      await docsApi.deleteDocument(docId);
      toast.success('Documentation deleted');
      if (selectedDocId === docId) {
        handleBackToCatalog();
      } else {
        loadCatalog();
      }
    } catch (err) {
      toast.error(err.message || 'Failed to delete documentation');
    }
  };

  const handleUploadSuccess = (newDoc) => {
    loadCatalog();
    loadModules();
    if (selectedDocId === newDoc?.id) {
      loadDocumentDetail(newDoc.id, newDoc.currentVersion);
    }
  };

  const handleGlobalSearch = async () => {
    try {
      setIsLoadingGlobal(true);
      const res = await docsApi.searchEndpoints({
        query: globalQuery.trim() || undefined,
        method: globalMethod !== 'ALL' ? globalMethod : undefined,
      });
      setGlobalEndpoints(res?.content || []);
    } catch (err) {
      toast.error(err.message || 'Search failed');
    } finally {
      setIsLoadingGlobal(false);
    }
  };

  const filteredEndpoints = (currentDocDetail?.endpoints || []).filter((ep) => {
    if (endpointMethodFilter !== 'ALL' && ep.httpMethod?.toUpperCase() !== endpointMethodFilter) {
      return false;
    }
    if (endpointTagFilter !== 'ALL') {
      const tags = (ep.tags || '').split(',').map((t) => t.trim());
      if (!tags.includes(endpointTagFilter)) return false;
    }
    if (endpointSearch.trim()) {
      const q = endpointSearch.toLowerCase().trim();
      const matchPath = ep.path?.toLowerCase().includes(q);
      const matchSummary = (ep.summary || '').toLowerCase().includes(q);
      const matchDesc = (ep.description || '').toLowerCase().includes(q);
      const matchTags = (ep.tags || '').toLowerCase().includes(q);
      if (!matchPath && !matchSummary && !matchDesc && !matchTags) return false;
    }
    return true;
  });

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto space-y-6 font-sans">
      {/* ────────────────── PAGE HEADER ────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200 pb-5">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2.5">
            <FiBookOpen className="text-blue-600 text-2xl" />
            API Documentation
          </h1>
          <p className="mt-1 text-sm text-slate-500 font-normal">
            Upload, interactively test, and manage OpenAPI specifications across ERP modules.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 w-full sm:w-auto shrink-0">
          {activeViewMode !== 'catalog' && (
            <button
              onClick={handleBackToCatalog}
              className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 shadow-sm transition-colors cursor-pointer"
            >
              <FiArrowLeft size={15} /> Back to Catalog
            </button>
          )}

          <button
            onClick={() => {
              setNewVersionTargetDoc(null);
              setIsUploadModalOpen(true);
            }}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition-colors cursor-pointer"
          >
            <FiUpload size={15} /> Upload API Documentation
          </button>
        </div>
      </div>

      {/* ────────────────── VIEW 1: CATALOG VIEW ────────────────── */}
      {activeViewMode === 'catalog' && (
        <div className="space-y-6">
          {/* Search, Filter Toolbar & Tabs */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm space-y-4">
            <div className="flex flex-col md:flex-row items-center gap-3">
              {/* Search input */}
              <div className="relative flex-1 w-full">
                <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadCatalog()}
                  placeholder="Search APIs by name, endpoint path, description, or tags..."
                  className="w-full pl-10 pr-4 py-2 bg-slate-50 hover:bg-white focus:bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 transition"
                />
              </div>

              {/* Module Filter */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full md:w-auto">
                <select
                  value={selectedModule}
                  onChange={(e) => setSelectedModule(e.target.value)}
                  className="w-full sm:w-44 text-sm px-3 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 transition cursor-pointer"
                >
                  <option value="all">All Modules</option>
                  {availableModules.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>

                {/* Visibility Filter */}
                <select
                  value={selectedVisibility}
                  onChange={(e) => setSelectedVisibility(e.target.value)}
                  className="w-full sm:w-40 text-sm px-3 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 transition cursor-pointer"
                >
                  <option value="all">All Visibility</option>
                  <option value="ORGANIZATION">Organization</option>
                  <option value="PRIVATE">Private</option>
                </select>

                <button
                  onClick={loadCatalog}
                  className="p-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 shadow-sm transition shrink-0 cursor-pointer flex items-center justify-center"
                  title="Refresh Catalog"
                >
                  <FiRefreshCw className={isLoadingDocs ? 'animate-spin' : ''} size={15} />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between text-xs text-slate-500 pt-3 border-t border-slate-100">
              <span>
                Showing <strong className="text-slate-800">{documents.length}</strong> API documentation {documents.length === 1 ? 'entry' : 'entries'}
              </span>

              <button
                onClick={() => {
                  setActiveViewMode('global_endpoints');
                  handleGlobalSearch();
                }}
                className="inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-800 font-semibold transition"
              >
                <FiLayers size={14} />
                <span>Search Across All Endpoints</span>
              </button>
            </div>
          </div>

          {/* Catalog Cards Grid */}
          {isLoadingDocs ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-56 rounded-xl bg-white border border-slate-200 p-6 animate-pulse space-y-4">
                  <div className="h-4 bg-slate-100 rounded w-1/3"></div>
                  <div className="h-6 bg-slate-100 rounded w-3/4"></div>
                  <div className="h-12 bg-slate-100 rounded"></div>
                  <div className="h-8 bg-slate-100 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : documents.length === 0 ? (
            <div className="p-12 text-center rounded-xl border border-slate-200 bg-white shadow-sm space-y-3">
              <div className="w-12 h-12 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mx-auto">
                <FiBookOpen size={22} />
              </div>
              <h3 className="text-base font-bold text-slate-900">No API Documentation Found</h3>
              <p className="text-sm text-slate-500 max-w-md mx-auto">
                {searchQuery
                  ? `No documentation matched "${searchQuery}". Try adjusting your search query or module filters.`
                  : 'Upload an OpenAPI JSON specification file to automatically generate interactive documentation.'}
              </p>
              <button
                onClick={() => {
                  setNewVersionTargetDoc(null);
                  setIsUploadModalOpen(true);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-700 text-white mt-2 transition"
              >
                <FiUpload size={15} /> Upload First API Documentation
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex flex-col justify-between rounded-xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-md hover:border-slate-300 transition-all duration-200"
                >
                  <div className="space-y-3">
                    {/* Header Row: Module badge & Visibility pill */}
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200/60">
                        {doc.moduleName || 'General'}
                      </span>

                      <span
                        className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-0.5 rounded-full border ${
                          doc.visibility === 'ORGANIZATION'
                            ? 'bg-blue-50 text-blue-700 border-blue-200/60'
                            : 'bg-amber-50 text-amber-700 border-amber-200/60'
                        }`}
                      >
                        {doc.visibility === 'ORGANIZATION' ? <FiShield size={12} /> : <FiLock size={12} />}
                        <span>{doc.visibility === 'ORGANIZATION' ? 'Organization' : 'Private'}</span>
                      </span>
                    </div>

                    {/* API Title & Version */}
                    <div>
                      <div className="flex items-center gap-2">
                        <h3
                          onClick={() => handleOpenDoc(doc.id)}
                          className="text-base font-bold text-slate-900 hover:text-blue-600 cursor-pointer transition line-clamp-1"
                        >
                          {doc.title}
                        </h3>
                        <span className="font-mono text-xs font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200 shrink-0">
                          v{doc.currentVersion}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 line-clamp-2 mt-2 leading-relaxed">
                        {doc.description || 'No description provided.'}
                      </p>
                    </div>
                  </div>

                  {/* Card Bottom / Footer */}
                  <div className="pt-4 border-t border-slate-100 mt-5 space-y-3">
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span>
                        <strong className="text-slate-800">{doc.endpointsCount}</strong> endpoints · <strong className="text-slate-800">{doc.versionsCount}</strong> {doc.versionsCount === 1 ? 'version' : 'versions'}
                      </span>
                      <span>By {doc.ownerName || 'Admin'}</span>
                    </div>

                    <div className="flex items-center justify-between gap-2">
                      <button
                        onClick={() => handleOpenDoc(doc.id)}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition"
                      >
                        <span>Explore</span>
                        <FiChevronRight size={13} />
                      </button>

                      <div className="flex items-center gap-1.5">
                        {doc.canShare && (
                          <button
                            onClick={() => setShareTargetDoc(doc)}
                            className="p-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 transition"
                            title="Manage Access & Sharing"
                          >
                            <FiUsers size={14} />
                          </button>
                        )}

                        {doc.canDownload && (
                          <button
                            onClick={async () => {
                              try {
                                await docsApi.downloadFullDocument(doc.id, doc.currentVersion);
                                toast.success('Downloaded full OpenAPI JSON');
                              } catch (err) {
                                toast.error('Download failed');
                              }
                            }}
                            className="p-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 transition"
                            title="Download Full OpenAPI JSON"
                          >
                            <FiDownload size={14} />
                          </button>
                        )}

                        {doc.canEdit && (
                          <button
                            onClick={() => handleDeleteDocument(doc.id)}
                            className="p-2 rounded-lg border border-rose-200 bg-white hover:bg-rose-50 text-rose-600 hover:text-rose-700 transition"
                            title="Delete Documentation"
                          >
                            <FiTrash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ────────────────── VIEW 2: INTERACTIVE DETAIL VIEW ────────────────── */}
      {activeViewMode === 'detail' && (
        <div className="space-y-6">
          {isLoadingDetail || !currentDocDetail ? (
            <div className="h-96 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-slate-400">
              <FiRefreshCw className="animate-spin text-2xl mr-2 text-blue-600" />
              <span>Loading documentation details...</span>
            </div>
          ) : (
            <>
              {/* Document Info Card */}
              <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm space-y-5">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="text-xs font-bold uppercase tracking-wider px-2.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200/60">
                        {currentDocDetail.moduleName || 'General'}
                      </span>
                      <span
                        className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-0.5 rounded-full border ${
                          currentDocDetail.visibility === 'ORGANIZATION'
                            ? 'bg-blue-50 text-blue-700 border-blue-200/60'
                            : 'bg-amber-50 text-amber-700 border-amber-200/60'
                        }`}
                      >
                        {currentDocDetail.visibility === 'ORGANIZATION' ? <FiShield size={12} /> : <FiLock size={12} />}
                        <span>{currentDocDetail.visibility === 'ORGANIZATION' ? 'Organization' : 'Private'}</span>
                      </span>
                    </div>

                    <h2 className="text-2xl font-bold text-slate-900">{currentDocDetail.title}</h2>
                    <p className="text-sm text-slate-500 mt-1 max-w-3xl leading-relaxed">
                      {currentDocDetail.description || 'No description provided.'}
                    </p>
                  </div>

                  {/* Actions & Version Selector */}
                  <div className="flex flex-wrap items-center gap-2.5 shrink-0">
                    {/* Version Selector */}
                    <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 text-xs">
                      <span className="text-slate-500 font-medium">Version:</span>
                      <select
                        value={selectedVersion}
                        onChange={(e) => setSelectedVersion(e.target.value)}
                        className="bg-transparent font-semibold text-slate-800 focus:outline-none cursor-pointer"
                      >
                        {(currentDocDetail.versions || []).map((v) => (
                          <option key={v.id} value={v.versionNumber}>
                            v{v.versionNumber} ({v.endpointsCount} endpoints)
                          </option>
                        ))}
                      </select>
                    </div>

                    {currentDocDetail.canEdit && (
                      <button
                        onClick={() => {
                          setNewVersionTargetDoc(currentDocDetail);
                          setIsUploadModalOpen(true);
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 shadow-sm transition"
                      >
                        <FiPlus size={14} /> New Version
                      </button>
                    )}

                    {currentDocDetail.canDownload && (
                      <button
                        onClick={handleDownloadFull}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 shadow-sm transition"
                        title="Download Full OpenAPI JSON"
                      >
                        <FiDownload size={14} /> Download Full Spec
                      </button>
                    )}

                    {currentDocDetail.canShare && (
                      <button
                        onClick={() => setShareTargetDoc(currentDocDetail)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 shadow-sm transition"
                      >
                        <FiUsers size={14} /> Sharing
                      </button>
                    )}
                  </div>
                </div>

                {/* View Mode Toggle: Interactive Explorer vs Swagger UI */}
                <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                  <div className="inline-flex items-center p-1 rounded-lg bg-slate-100 border border-slate-200">
                    <button
                      onClick={() => setDocDisplayMode('explorer')}
                      className={`px-3.5 py-1.5 rounded-md text-xs font-semibold transition ${
                        docDisplayMode === 'explorer'
                          ? 'bg-white text-slate-900 shadow-sm'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      Interactive Explorer
                    </button>
                    <button
                      onClick={() => setDocDisplayMode('swagger')}
                      className={`px-3.5 py-1.5 rounded-md text-xs font-semibold transition ${
                        docDisplayMode === 'swagger'
                          ? 'bg-white text-slate-900 shadow-sm'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      Swagger UI
                    </button>
                  </div>

                  <span className="text-xs text-slate-500 font-medium">
                    Owner: <strong>{currentDocDetail.ownerName || currentDocDetail.ownerEmail}</strong>
                  </span>
                </div>
              </div>

              {/* Toggle 1: SWAGGER UI */}
              {docDisplayMode === 'swagger' && (
                <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm overflow-hidden">
                  <SwaggerUiViewer specJson={currentDocDetail.openApiJson} />
                </div>
              )}

              {/* Toggle 2: NATIVE EXPLORER */}
              {docDisplayMode === 'explorer' && (
                <div className="space-y-4">
                  {/* Endpoints Filter Bar */}
                  <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm flex flex-col md:flex-row items-center gap-3">
                    <div className="relative flex-1 w-full">
                      <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
                      <input
                        type="text"
                        value={endpointSearch}
                        onChange={(e) => setEndpointSearch(e.target.value)}
                        placeholder="Filter endpoints by path, description, or tag..."
                        className="w-full pl-10 pr-4 py-2 bg-slate-50 hover:bg-white focus:bg-white border border-slate-200 rounded-lg text-xs text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 transition"
                      />
                    </div>

                    {/* Method Filter Pills */}
                    <div className="flex items-center gap-1 overflow-x-auto w-full md:w-auto pb-1 md:pb-0">
                      {['ALL', 'GET', 'POST', 'PUT', 'DELETE', 'PATCH'].map((m) => (
                        <button
                          key={m}
                          onClick={() => setEndpointMethodFilter(m)}
                          className={`px-2.5 py-1 rounded-md text-xs font-bold font-mono transition ${
                            endpointMethodFilter === m
                              ? 'bg-blue-600 text-white shadow-sm'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {m}
                        </button>
                      ))}
                    </div>

                    {/* Tag Filter */}
                    {currentDocDetail.availableTags?.length > 0 && (
                      <select
                        value={endpointTagFilter}
                        onChange={(e) => setEndpointTagFilter(e.target.value)}
                        className="w-full md:w-40 text-xs px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-700 focus:outline-none cursor-pointer"
                      >
                        <option value="ALL">All Tags</option>
                        {currentDocDetail.availableTags.map((tag) => (
                          <option key={tag} value={tag}>{tag}</option>
                        ))}
                      </select>
                    )}
                  </div>

                  {/* Endpoints Count Bar */}
                  <div className="flex items-center justify-between text-xs text-slate-500 px-1">
                    <span>
                      Showing <strong className="text-slate-800">{filteredEndpoints.length}</strong> of{' '}
                      <strong>{currentDocDetail.endpoints?.length || 0}</strong> endpoints
                    </span>
                  </div>

                  {/* Endpoints List */}
                  {filteredEndpoints.length === 0 ? (
                    <div className="p-8 text-center rounded-xl border border-slate-200 bg-white text-slate-500 text-sm">
                      No endpoints match the current filter criteria.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredEndpoints.map((endpoint) => (
                        <ApiEndpointCard
                          key={endpoint.id}
                          endpoint={endpoint}
                          documentId={currentDocDetail.id}
                          canDownload={currentDocDetail.canDownload}
                          canTest={currentDocDetail.canTest}
                          onDownloadEndpoint={handleDownloadEndpoint}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ────────────────── VIEW 3: GLOBAL ENDPOINTS SEARCH ────────────────── */}
      {activeViewMode === 'global_endpoints' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-4">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Global Endpoints Search</h2>
              <p className="text-xs text-slate-500 mt-1">
                Instantly search across indexed endpoints from all accessible ERP API documentations.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3">
              <div className="relative flex-1 w-full">
                <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <input
                  type="text"
                  value={globalQuery}
                  onChange={(e) => setGlobalQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleGlobalSearch()}
                  placeholder="Search endpoint path, summary, description, or tags..."
                  className="w-full pl-10 pr-4 py-2 bg-slate-50 hover:bg-white focus:bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 transition"
                />
              </div>

              <div className="flex items-center gap-1 overflow-x-auto w-full sm:w-auto">
                {['ALL', 'GET', 'POST', 'PUT', 'DELETE', 'PATCH'].map((m) => (
                  <button
                    key={m}
                    onClick={() => setGlobalMethod(m)}
                    className={`px-2.5 py-1.5 rounded-md text-xs font-bold font-mono transition ${
                      globalMethod === m
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>

              <button
                onClick={handleGlobalSearch}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition w-full sm:w-auto"
              >
                Search
              </button>
            </div>
          </div>

          {/* Results */}
          {isLoadingGlobal ? (
            <div className="h-64 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-slate-400 text-sm">
              <FiRefreshCw className="animate-spin text-xl mr-2 text-blue-600" />
              Searching indexed endpoints...
            </div>
          ) : globalEndpoints.length === 0 ? (
            <div className="p-8 text-center rounded-xl border border-slate-200 bg-white text-slate-500 text-sm">
              No matching endpoints found. Try a different query or method filter.
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm divide-y divide-slate-100 overflow-hidden">
              {globalEndpoints.map((ep) => (
                <div
                  key={ep.id}
                  className="p-4 hover:bg-slate-50/70 transition flex items-center justify-between gap-4"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2.5">
                      <span className="px-2 py-0.5 rounded text-xs font-mono font-bold bg-slate-100 text-slate-700 border border-slate-200">
                        {ep.httpMethod}
                      </span>
                      <span className="font-mono text-sm font-semibold text-slate-900">{ep.path}</span>
                      {ep.tags && (
                        <span className="text-[11px] px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200/60 font-medium">
                          {ep.tags}
                        </span>
                      )}
                    </div>
                    {ep.summary && <p className="text-xs text-slate-600">{ep.summary}</p>}
                    {ep.documentTitle && (
                      <p className="text-[11px] text-slate-400">
                        Document: <strong className="text-slate-600">{ep.documentTitle}</strong>
                      </p>
                    )}
                  </div>

                  <button
                    onClick={() => handleOpenDoc(ep.documentId)}
                    className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 transition shrink-0"
                  >
                    <span>View In Doc</span>
                    <FiChevronRight size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ────────────────── MODALS ────────────────── */}
      {isUploadModalOpen && (
        <ApiUploadModal
          existingDoc={newVersionTargetDoc}
          onClose={() => {
            setIsUploadModalOpen(false);
            setNewVersionTargetDoc(null);
          }}
          onSuccess={handleUploadSuccess}
        />
      )}

      {shareTargetDoc && (
        <ApiShareModal
          doc={shareTargetDoc}
          onClose={() => setShareTargetDoc(null)}
        />
      )}
    </div>
  );
}

export default DocumentationPortal;
